import {
	boolean,
	equal,
	integer,
	min,
	length,
	not,
	required,
	string,
} from 'vue-mc/validation';
import Model from './Model';
import Collection from './Collection';
import skipValidation from './validators/skip';

/**
 * Project model
 *
 * @param {number} id
 * @param {string} name
 * @param {number} accountId
 * @param {boolean} isArchived
 * @param {Object} stats Include 'activeReportsCount' and 'activeArticlesCount'
 */
export class Project extends Model {

	// Default attributes that define the "empty" state.
	defaults() {
		return {
			id: null,
			name: '',
			accountId: null,
			isArchived: false,
			activeCategories: [],
			archivedCategories: [],
			customDomain: null,
			customFields: [],
			permissions: [],
			/** @todo Send users allow for this project from backend with same request */
			users: [],
			outputs: [],
			keywordLists: [],
			customDatas: [],
			overrides: [],
			stats: {
				activeArticlesCount: 0,
				activeReportsCount: 0,
			},
		};
	}

	// Attribute mutations.
	mutations() {
		return {
			id: id => Number(id) || null,
			name: String,
			isArchived: Boolean,
		};
	}

	// Attribute validation
	validation() {
		const activeProjects = this.$store.get('projects/active').filter(m => m.id !== this.id);

		return {
			id: integer.and(min(1)).or(equal(null)),
			name: skipValidation.or(
				required.and(string).and(length(1, 35)).and(not(...activeProjects.getNames())),
			),
			isArchived: boolean,
		};
	}

	// Route configuration
	routes() {
		return {
			create: '/projects',
			fetch: '/projects/{id}',
			patch: '/projects/{id}',
			delete: '/projects/{id}',
			uploadImage: 'projects/{id}/upload-image',
			fetchUsers: '/projects/{id}/users',
			updateUser: '/projects/{id}/users',
		};
	}

	options() {
		return {
			validateOnChange: true,
			patch: true,
			methods: {
				uploadImage: 'POST',
				fetchUsers: 'GET',
				updateUser: 'PATCH',
			},
		};
	}

	uploadImage(uri) {
		const method = this.getOption('methods.uploadImage');
		const route = this.getRoute('uploadImage');
		const url = this.getURL(route, this.getRouteParameters());

		return new Promise((resolve, reject) => {
			this.loading = true;
			this.getRequest({ method,
				url,
				headers: { Authorization: '' },
				data: { uri },
			})
				.send()
				.then(({ response }) => {
					this.image = response.data.image;
					this.images = response.data.images;
					resolve(response);
				}).catch((error) => {
					reject(error);
				})
				.finally(() => {
					this.loading = false;
				});
		});
	}

	/**
	 * Fetches users, who have access to this project and assigns to this.users
	 * @returns {Promise}
	 */
	fetchUsers() {
		const method = this.getOption('methods.fetchUsers');
		const route = this.getRoute('fetchUsers');
		const url = this.getURL(route, this.getRouteParameters());

		return new Promise((resolve, reject) => {
			this.loading = true;
			this.getRequest({ method, url, headers: { Authorization: '' } })
				.send()
				.then((r) => {
					resolve(r);
				}).catch((e) => {
					reject(e);
				})
				.finally(() => {
					this.loading = false;
				});
		});
	}

	updateUser(userId, accessLevel) {
		const method = this.getOption('methods.updateUser');
		const route = this.getRoute('updateUser');
		const url = this.getURL(route, this.getRouteParameters());

		return new Promise((resolve, reject) => {
			this.loading = true;
			this.getRequest({
				method,
				url,
				headers: { Authorization: '' },
				data: { userId, accessLevel } })
				.send()
				.then((r) => {
					resolve(r);
				}).catch((e) => {
					reject(e);
				})
				.finally(() => {
					this.loading = false;
				});
		});
	}

	get url() {
		return {
			name: 'ReportsList',
			params: {
				projectId: this.id,
			},
		};
	}

	get sortedOutputs() {
		return this.outputs.sort((a, b) => {
			if (a.default > b.default) return -1;
			if (a.default < b.default) return 1;

			if (a.name.toLowerCase() > b.name.toLowerCase())
				return 1;
			else if (a.name.toLowerCase() < b.name.toLowerCase())
				return -1;

			return 0;
		});
	}

	/**
	 * Returns true if current user has passed permission for this project, false otherwise
	 *
	 * @param {string} permissionName Name of permission
	 * @returns {boolean} If user has permission
	 */
	userCan(permissionName) {
		const permissions = this.permissions;
		if (!Array.isArray(permissions)) {
			dump('Permissions is not array', this.permissions);
			return false;
		}

		return (permissions.indexOf(permissionName) !== -1);
	}
}

/**
 * Projects collection
 */
export class Projects extends Collection {

	// Model that is contained in this collection.
	model() {
		return Project;
	}

	// Route configuration
	routes() {
		return {
			fetch: '/projects',
		};
	}

	/**
	 * @returns {string[]} Array of project name in the collection
	 */
	getNames() {
		return this.map(project => project.name);
	}

}

export class ActiveProjects extends Projects {

	routes() {
		return {
			fetch: '/projects?archived=0',
		};
	}
}

export class ArchivedProjects extends Projects {

	routes() {
		return {
			fetch: '/projects?archived=1',
		};
	}
}
